import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = '90bdcb4c1955a7aa79a5acdabf104886';
const IS_DEV = import.meta.env.DEV;

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const devLog = (...args: unknown[]) => { if (IS_DEV) console.log(...args); };

let initialized = false;

export function initAnalytics() {
  if (initialized) return;
  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: IS_DEV,
      track_pageview: false,
      persistence: 'localStorage',
      autocapture: true,
      record_sessions_percent: 10,
      // Mask password + email inputs and anything explicitly marked data-private
      // inside session replays so PII never leaves the browser.
      record_mask_text_selector: 'input[type="password"], input[type="email"], [data-private]',
      record_block_selector: '[data-replay-block]',
      api_host: 'https://api-eu.mixpanel.com',
      // Respect user's Do-Not-Track browser setting.
      ignore_dnt: false,
    });
    initialized = true;
    devLog('[Analytics] Mixpanel initialized (EU endpoint)');
    captureUtmParams();
  } catch (err) {
    if (IS_DEV) console.error('[Analytics] Mixpanel init failed:', err);
  }
}

export function identifyUser(user: {
  id: string;
  email?: string | null;
  name?: string | null;
  isAdmin?: boolean;
}) {
  if (!initialized) return;
  try {
    mixpanel.identify(user.id);
    mixpanel.people.set({
      $email: user.email ?? undefined,
      $name: user.name ?? user.email ?? undefined,
      is_admin: user.isAdmin ?? false,
    });
    devLog('[Analytics] User identified:', user.id);
  } catch (err) {
    if (IS_DEV) console.error('[Analytics] identify failed:', err);
  }
}

/**
 * Identify a Cold-Email lead visiting a Tryout-Link.
 * Sets the lead's email as the Mixpanel distinct_id so anonymous tryout activity
 * is attributed to the actual person, and registers super-properties so all
 * subsequent events on this device session carry the lead context.
 */
export function identifyTryoutLead(lead: {
  email: string;
  firstName: string | null;
  fullName: string | null;
  company: string | null;
  location: string | null;
  bundesland: string | null;
  berufsgruppe: string | null;
  list_id: string | null;
  campaign_name: string | null;
  source: string | null;
  token: string;
}) {
  if (!initialized) return;
  try {
    mixpanel.identify(lead.email);
    mixpanel.people.set({
      $email: lead.email,
      $name: lead.fullName ?? lead.firstName ?? lead.email,
      $first_name: lead.firstName ?? undefined,
      company: lead.company ?? undefined,
      city: lead.location ?? undefined,
      bundesland: lead.bundesland ?? undefined,
      berufsgruppe: lead.berufsgruppe ?? undefined,
      cold_email_list_id: lead.list_id ?? undefined,
      cold_email_campaign: lead.campaign_name ?? undefined,
      acquisition_source: lead.source ?? undefined,
      first_tryout_token: lead.token,
    });
    mixpanel.register({
      list_id: lead.list_id ?? undefined,
      campaign_name: lead.campaign_name ?? undefined,
      berufsgruppe: lead.berufsgruppe ?? undefined,
      city: lead.location ?? undefined,
      company: lead.company ?? undefined,
      tryout_token: lead.token,
    });
    devLog('[Analytics] Tryout lead identified:', lead.email);
  } catch (err) {
    if (IS_DEV) console.error('[Analytics] identifyTryoutLead failed:', err);
  }
}

export function resetUser() {
  if (!initialized) return;
  try {
    mixpanel.reset();
  } catch (err) {
    if (IS_DEV) console.error('[Analytics] reset failed:', err);
  }
}

export function track(event: string, props?: Record<string, unknown>) {
  if (!initialized) return;
  try {
    mixpanel.track(event, props);
    devLog('[Analytics] Track:', event, props);
  } catch (err) {
    if (IS_DEV) console.error('[Analytics] track failed:', err);
  }
  window.dataLayer?.push({ event, ...props });
}

/**
 * Feuert ein client-only Event an die Klaviyo-Bridge (fire-and-forget, non-blocking).
 * No-op ohne eingeloggten User. Fehler werden geschluckt (nur DEV-Log).
 */
export function trackKlaviyo(
  event: 'Photo Uploaded' | 'Image Downloaded' | 'Logged In',
  props?: Record<string, unknown>,
): void {
  void (async () => {
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await supabase.functions.invoke('klaviyo-track', {
        body: { event, properties: props ?? {} },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
    } catch (err) {
      if (IS_DEV) console.error('[Analytics] trackKlaviyo failed:', err);
    }
  })();
}

const KLAVIYO_LOGIN_SESSION_KEY = 'klaviyo_login_fired';

/**
 * Feuert `Logged In` an Klaviyo — höchstens EINMAL pro Browser-Sitzung
 * (sessionStorage: überlebt Reloads im selben Tab, neu bei neuem Tab/Browser →
 * zählt Auto-Login/Session-Restore als neuen Login, aber nicht Token-Refreshes).
 */
export function trackKlaviyoLoginOncePerSession(login_method: string): void {
  try {
    if (sessionStorage.getItem(KLAVIYO_LOGIN_SESSION_KEY) === '1') return;
    sessionStorage.setItem(KLAVIYO_LOGIN_SESSION_KEY, '1');
  } catch {
    // sessionStorage nicht verfügbar → best-effort, trotzdem feuern
  }
  trackKlaviyo('Logged In', { login_method });
}

export function trackPageView(viewName: string) {
  track('Page Viewed', {
    view_name: viewName,
    page_url: window.location.href,
    page_title: document.title,
  });
}

const UTM_STORAGE_KEY = 'estage_utm_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export interface StoredUtmAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  captured_at: string;
}

function captureUtmParams() {
  const params = new URLSearchParams(window.location.search);
  const utmData: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) utmData[key] = val;
  }
  if (Object.keys(utmData).length > 0) {
    mixpanel.register(utmData);
    mixpanel.people.set_once(utmData);
    // Persist for signup-time attribution (Stripe webhook reads from profile).
    // set_once semantics: don't overwrite an earlier first-touch.
    try {
      const existing = localStorage.getItem(UTM_STORAGE_KEY);
      if (!existing) {
        const payload: StoredUtmAttribution = { ...utmData, captured_at: new Date().toISOString() };
        localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
      }
    } catch {
      /* localStorage may be unavailable in private mode — silently skip */
    }
  }
}

/**
 * Read first-touch UTM attribution captured at landing time.
 * Used by AuthContext to persist UTMs on the profile after signup so the
 * Stripe webhook can attribute Credit Purchased events back to the campaign.
 */
export function getStoredUtmAttribution(): StoredUtmAttribution | null {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredUtmAttribution;
  } catch {
    return null;
  }
}

export function clearStoredUtmAttribution(): void {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
  } catch { /* noop */ }
}

// ---------------------------------------------------------------------------
// Tryout-Page-View interaction-gate
// ---------------------------------------------------------------------------
// Cold-email B2B traffic gets pre-fetched by Microsoft Safe Links scanners
// (Dublin/Boydton) and Outlook preview bots. They run JS, fire mixpanel.track,
// and look indistinguishable from real visits. The interaction-gate buys us a
// soft heuristic: scanners hit-and-leave in <1s; humans stay >2s OR move
// the mouse. We only fire the event when one of those is true, AND we hit
// the server-side track-email-click endpoint with the user_interacted flag
// so the Postgres truth-source can apply the same filter.
//
// Returns a cleanup function (cancel pending timers / listeners).
interface GateOptions {
  /** Tryout token from URL — required to attribute the click server-side. */
  token: string;
  /** Lead email if already known on this device (super-property). */
  leadEmail?: string | null;
  /** Extra event properties forwarded to Mixpanel client-side. */
  extraProps?: Record<string, unknown>;
  /** Time before auto-firing if no interaction detected. */
  delayMs?: number;
}

export function gateTryoutPageView(opts: GateOptions): () => void {
  const { token, leadEmail, extraProps, delayMs = 2000 } = opts;

  let fired = false;
  let userInteracted = false;

  const fire = (interacted: boolean) => {
    if (fired) return;
    fired = true;
    userInteracted = interacted;

    // Client-side Mixpanel track (will inherit super-properties incl. UTM).
    track('Tryout Page Viewed', {
      token,
      user_interacted: interacted,
      ...(extraProps ?? {}),
    });

    // Server-side write — non-blocking, fire-and-forget. The Edge Function
    // does the bot classification (UA + IP + country) that the browser can't.
    void postEmailClick({
      token,
      lead_email: leadEmail ?? undefined,
      utm_source: getStoredUtmAttribution()?.utm_source,
      utm_medium: getStoredUtmAttribution()?.utm_medium,
      utm_campaign: getStoredUtmAttribution()?.utm_campaign,
      utm_content: getStoredUtmAttribution()?.utm_content,
      landing_url: window.location.href,
      referrer: document.referrer || undefined,
      user_interacted: interacted,
    });
  };

  const onInteract = () => fire(true);
  const timer = window.setTimeout(() => fire(false), delayMs);

  window.addEventListener('mousemove', onInteract, { once: true, passive: true });
  window.addEventListener('touchstart', onInteract, { once: true, passive: true });
  window.addEventListener('scroll', onInteract, { once: true, passive: true });
  window.addEventListener('keydown', onInteract, { once: true });

  return () => {
    window.clearTimeout(timer);
    window.removeEventListener('mousemove', onInteract);
    window.removeEventListener('touchstart', onInteract);
    window.removeEventListener('scroll', onInteract);
    window.removeEventListener('keydown', onInteract);
    // userInteracted is captured for diagnostics; suppress unused-var warning.
    void userInteracted;
  };
}

// ---------------------------------------------------------------------------
// Server-side click forwarding
// ---------------------------------------------------------------------------
interface PostEmailClickArgs {
  token?: string;
  lead_email?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  landing_url?: string;
  referrer?: string;
  user_interacted?: boolean;
}

async function postEmailClick(args: PostEmailClickArgs): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !anonKey) return;

  try {
    await fetch(`${supabaseUrl}/functions/v1/track-email-click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${anonKey}`,
      },
      body: JSON.stringify(args),
      // keepalive lets the call complete even if the user navigates away
      // immediately after the gate fires.
      keepalive: true,
    });
  } catch (err) {
    if (IS_DEV) console.error('[Analytics] postEmailClick failed (non-fatal):', err);
  }
}
